import axios from 'axios';
import { API_SETTINGS, getApiUrl } from '../config/apiSettings';

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_SETTINGS.API_URL,
  timeout: API_SETTINGS.REQUEST_CONFIG.TIMEOUT,
  headers: API_SETTINGS.HEADERS.DEFAULT,
});

// Add request interceptor for auth and logging
apiClient.interceptors.request.use(
  config => {
    // Add auth token if available
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    
    // Log requests in development
    if (process.env.NODE_ENV !== 'production') {
      console.log('🚀 Request:', {
        url: config.url,
        method: config.method,
        data: config.data
      });
    }
    return config;
  },
  error => Promise.reject(error)
);

// Add response interceptor for error handling and logging
apiClient.interceptors.response.use(
  response => {
    // Log responses in development
    if (process.env.NODE_ENV !== 'production') {
      console.log('✅ Response:', {
        url: response.config.url,
        status: response.status,
        data: response.data
      });
    }
    return response;
  },
  async error => {
    // Log errors in development
    if (process.env.NODE_ENV !== 'production') {
      console.error('❌ Error:', {
        url: error.config?.url,
        message: error.message,
        response: error.response?.data
      });
    }

    // Extract config, attempt count, and max retries
    const { config } = error;
    const retryAttempt = config._retryAttempt || 0;
    const maxRetries = API_SETTINGS.REQUEST_CONFIG.RETRY_ATTEMPTS;
    
    // Check if we should retry the request
    if (
      config && 
      retryAttempt < maxRetries && 
      (error.code === 'ECONNABORTED' || 
       error.message.includes('timeout') || 
       error.message.includes('Network Error') ||
       (error.response && [408, 429, 500, 502, 503, 504].includes(error.response.status)))
    ) {
      // Increase retry attempt
      config._retryAttempt = retryAttempt + 1;
      
      // Set retry delay
      const delayMs = API_SETTINGS.REQUEST_CONFIG.RETRY_DELAY * Math.pow(2, retryAttempt);
      
      // Log retry attempt
      console.log(`Retrying request (${config._retryAttempt}/${maxRetries}) after ${delayMs}ms`);
      
      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, delayMs));
      
      // Retry the request
      return apiClient(config);
    }
    
    // If we shouldn't retry or max retries reached, reject with error
    return Promise.reject(error);
  }
);

// Create service with categorized API calls
const apiService = {
  // Auth related API calls
  auth: {
    login: (credentials) => 
      apiClient.post(API_SETTINGS.ENDPOINTS.AUTH.LOGIN, credentials),
    
    register: (userData) => 
      apiClient.post(API_SETTINGS.ENDPOINTS.AUTH.REGISTER, userData),
    
    logout: () => 
      apiClient.post(API_SETTINGS.ENDPOINTS.AUTH.LOGOUT),
    
    resetPassword: (email) => 
      apiClient.post(API_SETTINGS.ENDPOINTS.AUTH.RESET_PASSWORD, { email }),
  },
  
  // Forms related API calls
  forms: {
    contact: (formData) => 
      apiClient.post(API_SETTINGS.ENDPOINTS.FORMS.CONTACT, formData),
    
    newsletter: (data) => {
      console.log('Newsletter API call with data:', data);
      
      const payload = {
        email: data.email,
        name: data.name || '',
        listType: data.listType || 'GENERAL'
      };
      
      return apiClient.post(API_SETTINGS.ENDPOINTS.FORMS.NEWSLETTER, payload);
    },
    
    exhibition: (formData) => 
      apiClient.post(API_SETTINGS.ENDPOINTS.FORMS.EXHIBITION, formData),
    
    filmScreening: (formData) => 
      apiClient.post(API_SETTINGS.ENDPOINTS.FORMS.FILM_SCREENING, formData),
    
    financierAccreditation: (formData) => 
      apiClient.post(API_SETTINGS.ENDPOINTS.FORMS.FINANCIER_ACCREDITATION, formData),
    
    masterclassEnrollment: (formData) => 
      apiClient.post(API_SETTINGS.ENDPOINTS.FORMS.MASTERCLASS_ENROLLMENT, formData),
  
    
    submitIndustryAccreditation: (formData) =>
      apiClient.post(API_SETTINGS.ENDPOINTS.FORMS.INDUSTRY_ACCREDITATION, formData),
    
    pitchSubmission: (formData) => {
      // Create FormData object for file uploads
      const formDataObj = new FormData();
      
      // Append text fields
      Object.keys(formData).forEach(key => {
        // Skip file fields
        if (formData[key] && !key.includes('File')) {
          formDataObj.append(key, formData[key]);
        }
      });
      
      // Append file fields
      Object.keys(formData).forEach(key => {
        if (key.includes('File') && formData[key]) {
          formDataObj.append(key, formData[key]);
        }
      });
      
      // Use multipart headers for file upload
      return apiClient.post(
        API_SETTINGS.ENDPOINTS.FORMS.PITCH_SUBMISSION, 
        formDataObj, 
        { headers: API_SETTINGS.HEADERS.MULTIPART }
      );
    },
    
    // Get draft application by reference code
    getDraftByReferenceCode: (referenceCode) =>
      apiClient.get(`/drafts/${referenceCode}`),
    
    // Save draft application
    saveDraft: (draftData) =>
      apiClient.post('/drafts', draftData),
  },
  
  // Payment related API calls
  payments: {
    initiate: (paymentData) =>
      apiClient.post(API_SETTINGS.ENDPOINTS.PAYMENTS.INITIATE, paymentData),
    
    verify: (reference) =>
      apiClient.get(`${API_SETTINGS.ENDPOINTS.PAYMENTS.VERIFY}/${reference}`),
    
    getHistory: () =>
      apiClient.get(API_SETTINGS.ENDPOINTS.PAYMENTS.HISTORY),
    
    updatePayment: (enrollmentId, paymentReference, status) =>
      apiClient.post('/payments/update', { enrollmentId, paymentReference, status }),
  },
  
  // Content related API calls
  content: {
    getMasterclasses: () =>
      apiClient.get(API_SETTINGS.ENDPOINTS.CONTENT.MASTERCLASSES),
    
    getMasterclassById: (id) =>
      apiClient.get(`${API_SETTINGS.ENDPOINTS.CONTENT.MASTERCLASSES}/${id}`),
    
    getSpeakers: () =>
      apiClient.get(API_SETTINGS.ENDPOINTS.CONTENT.SPEAKERS),
    
    getEvents: () =>
      apiClient.get(API_SETTINGS.ENDPOINTS.CONTENT.EVENTS),
    
    getDeals: () =>
      apiClient.get(API_SETTINGS.ENDPOINTS.CONTENT.DEALS),
  },
  
  // Utility methods
  utils: {
    getExchangeRates: (baseCurrency = 'USD') =>
      axios.get(`https://api.exchangerate-api.com/v4/latest/${baseCurrency}`),
  },
  
  // Add these methods at the top level
  saveAccreditationDraft: (draftData) => 
    apiClient.post('/drafts', draftData),
    
  submitFinancierAccreditation: (formData) =>
    apiClient.post(API_SETTINGS.ENDPOINTS.FORMS.FINANCIER_ACCREDITATION, formData),
    
  getAccreditationDraft: (referenceCode) =>
    apiClient.get(`/drafts/${referenceCode}`),

  // Add dedicated methods for pitch drafts
  savePitchDraft: async (draftData) => {
    return await apiClient.post('/drafts/pitch', draftData);
  },

  getPitchDraft: async (referenceCode) => {
    return await apiClient.get(`/drafts/pitch/${referenceCode}`);
  },

  // Fix the submitPitchApplication method
  submitPitchApplication: async (formData) => {
    try {
      console.log(`Making request to: ${API_SETTINGS.API_URL}/pitch-submission`);
      
      const response = await fetch(`${API_SETTINGS.API_URL}/pitch-submission`, {
        method: 'POST',
        body: formData,
        credentials: 'include', // Include cookies if needed
      });
      
      // Check if the response is ok before trying to parse JSON
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Server error response:', errorText);
        throw new Error(`Server responded with status: ${response.status}. ${errorText}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error submitting pitch application:', error);
      throw new Error(error.message || 'Failed to submit application. Please try again.');
    }
  },
};

export default apiService; 